/**
 * VAMS-544 — Pure utilities for PLP pagination (no dependency on React or the DOM).
 *
 * PAGE_SIZE is the single source of truth for the number of products per page.
 * The `24` literals in the Liquid templates (plp_seo_page_size in collection-seo-pagination.liquid
 * and seo_pagination_page_size in collection-seo-robots.liquid) MUST stay aligned with this value:
 * Liquid can't import TypeScript.
 */

export const PLP_PAGE_SIZE = 24

/**
 * Reads the `?page=N` parameter from the given URL (default: window.location.search).
 * Returns 1 for any invalid, non-integer, or ≤ 0 value.
 */
export const getPageFromUrl = (search = window.location.search): number => {
  const raw = new URLSearchParams(search).get('page')
  const page = raw ? parseInt(raw, 10) : 1

  return Number.isFinite(page) && page > 0 ? page : 1
}

/**
 * Total number of pages given the total product count.
 * Minimum 1 even with 0 products.
 */
export const totalPages = (totalProducts: number): number =>
  totalProducts > 0 ? Math.ceil(totalProducts / PLP_PAGE_SIZE) : 1

/**
 * Number of products that must have been loaded for page N to be
 * fully available. E.g.: page=1 → 24; page=2 → 48; page=3 → 72.
 */
export const productsNeededForPage = (page: number): number =>
  Math.max(page, 1) * PLP_PAGE_SIZE

/**
 * VAMS-544 (iter1) — Pure predicate to decide whether the deep-link can align to the target:
 * the card count has reached the target AND has been stable for at least N ticks (the
 * async sections have stopped inserting cards). Decoupled from the DOM and from `scrollHeight`,
 * which the anti-CLS height reservation pinned → causing premature settles on an incomplete DOM.
 */
export const isDeepLinkSettled = ({
  cardCount,
  targetIndex,
  stableTicks,
  requiredStableTicks,
}: {
  cardCount: number
  targetIndex: number
  stableTicks: number
  requiredStableTicks: number
}): boolean => cardCount > targetIndex && stableTicks >= requiredStableTicks
